import { CloseOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import { Form, Typography } from 'antd';
import { FormProps } from 'antd/lib';
import { Moment } from 'moment';
import React, { useState } from 'react';
import { Option } from '@nxsys/types/models/Common';
import AppRateInput from '@nxsys/components/AppRateInput';
import { hasFilterValue } from '@nxsys/utils/filterForm';

type FilterModalProps = {
  isOpen: boolean;
  toggle: () => void;
  onFilter: (params: FilterValues) => void;
  isBureau?: boolean;
};

export type FilterValues = {
  paymentDateFrom: Moment;
  paymentDateTo: Moment;
  taxPeriodFrom: number;
  taxPeriodTo: number;
  batchFrom: number;
  batchTo: number;
  safeRecStatuses: Option<string>[];
  veriPayeStatuses: Option<string>[];
  companies: Option<string>[];
};

const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  toggle,
  onFilter,
  isBureau,
}: FilterModalProps) => {
  const { messages } = useTypedIntl();

  const [form] = Form.useForm();
  const watchValue = Form.useWatch([], form);
  const hasFilter = hasFilterValue(watchValue);
  // Keep Apply enabled once a filter is applied, so Clear All + Apply can remove it.
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    form.resetFields();
  };

  const onFinish: FormProps['onFinish'] = (values) => {
    setHasAppliedFilter(hasFilterValue(values));
    onFilter(values);
    toggle();
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppModalContainer
      title={messages['common.filter']}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages['common.apply'],
          buttonType: 'primary',
          onClick: () => form.submit(),
          disabled: !hasFilter && !hasAppliedFilter,
        },
      ]}
      closeIcon={
        <div style={{ marginRight: 90 }}>
          <AppButton
            name={messages['common.clearAll']}
            leftIcon={<CloseOutlined />}
            onClick={handleClearAll as any}
            disabled={!hasFilter}
          />
        </div>
      }
      minHeight="200px"
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
      >
        <Typography.Text>{messages['common.paymentDate']}</Typography.Text>
        <div className="grid grid-cols-2  gap-x-4 mt-2">
          <Form.Item name="paymentDateFrom">
            <AppDatePicker
              placeholder={messages['common.fromDate']}
              onChange={(date: Moment) => {
                form.setFieldsValue({
                  paymentDateFrom: date,
                });
              }}
              format="DD/MM/YYYY"
              maxDate={watchValue?.paymentDateTo}
            />
          </Form.Item>
          <Form.Item name="paymentDateTo">
            <AppDatePicker
              placeholder={messages['common.toDate']}
              onChange={(date: Moment) => {
                form.setFieldsValue({
                  paymentDateTo: date,
                });
              }}
              format="DD/MM/YYYY"
              minDate={watchValue?.paymentDateFrom}
            />
          </Form.Item>
        </div>

        <Typography.Text>{messages['common.taxPeriod']}</Typography.Text>
        <div className="grid grid-cols-2  gap-x-4 mt-2">
          <Form.Item name="taxPeriodFrom">
            <AppRateInput placeholder={messages['common.from']} />
          </Form.Item>
          <Form.Item name="taxPeriodTo">
            <AppRateInput placeholder={messages['common.to']} />
          </Form.Item>
        </div>

        <Typography.Text>{messages['common.batch']}</Typography.Text>
        <div className="grid grid-cols-2  gap-x-4 mt-2">
          <Form.Item name="batchFrom">
            <AppRateInput placeholder={messages['common.from']} />
          </Form.Item>
          <Form.Item name="batchTo">
            <AppRateInput placeholder={messages['common.to']} />
          </Form.Item>
        </div>

        {isBureau ? (
          <Form.Item label={messages['common.employer']} name="companies">
            <AppSelectSync
              type="company"
              apiType="searchCompany"
              mode="multiple"
              placeholder={messages['form.pleaseSelect']}
              onChangeValue={(value) => {
                form.setFieldsValue({
                  companies: value,
                });
              }}
            />
          </Form.Item>
        ) : (
          <>
            <Typography.Text>{messages['common.safeRecStatus']}</Typography.Text>
            <div className="mt-2">
              <Form.Item name="safeRecStatuses">
                <AppSelect
                  mode="multiple"
                  allowClear
                  placeholder={messages['common.safeRecStatus']}
                  className="w-full"
                  options={[
                    { label: 'Success', value: 'SUCCESS' },
                    { label: 'Failed', value: 'FAILED' },
                    { label: 'Not Sent', value: 'NOT_SENT' },
                  ]}
                />
              </Form.Item>
            </div>

            <Typography.Text>{messages['common.veriPayeStatus']}</Typography.Text>
            <div className="mt-2">
              <Form.Item name="veriPayeStatuses">
                <AppSelect
                  mode="multiple"
                  allowClear
                  placeholder={messages['common.veriPayeStatus']}
                  className="w-full"
                  options={[
                    { label: 'Success', value: 'SUCCESS' },
                    { label: 'Failed', value: 'FAILED' },
                    { label: 'Not Sent', value: 'NOT_SENT' },
                  ]}
                />
              </Form.Item>
            </div>
          </>
        )}
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default FilterModal;
